import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

// Resolve one igloo's friendly name via the controller family's igloos
// subcollection (familyInfo/{controllerFamilyId}/igloos/{iglooId}.name), falling
// back to the iglooId when unresolved. Always returns an [iglooId, name] entry.
async function resolveOne(iglooId, controllerFamilyId) {
  if (!controllerFamilyId) return [iglooId, iglooId];
  try {
    const nameDoc = await getDoc(doc(db, 'familyInfo', controllerFamilyId, 'igloos', iglooId));
    return [iglooId, nameDoc.exists() ? (nameDoc.data().name || iglooId) : iglooId];
  } catch (e) {
    return [iglooId, iglooId];
  }
}

// Shared hook: builds a map of iglooId -> friendly name. Used by both the Health
// tab and the Device tab so the two share one implementation and can't drift.
//
// `enabled` gates all fetching: callers pass false when their tab isn't active so
// the scan doesn't run needlessly (the Device tab passes `activeTab === 'devices'`;
// the Health tab passes `true`). Returns {} until the first resolution completes.
//
// `neededIds` is the set of igloo IDs currently on screen. Two resolution paths,
// both merged into the same map:
//   1. Bulk scan of `iglooInfo` — once per mount, a warm-up that resolves every
//      igloo present when the tab opens (per-igloo name lookups run in parallel).
//   2. On-demand — after the scan, any `neededIds` still missing from the map are
//      resolved individually and merged in. This self-heals igloos created after
//      the initial scan (a device moving into a brand-new igloo) without
//      re-scanning the whole collection, and without showing the raw igloo ID.
//
// Stores merge (never replace) so the two paths can't clobber each other.
// `loadedRef` is set only after the scan actually stores, so a scan cancelled
// mid-flight or a hard failure does NOT suppress a later retry — it self-heals.
export function useIglooNames(enabled, neededIds = []) {
  const [iglooNames, setIglooNames] = useState({});
  const loadedRef = useRef(false);          // true only once the bulk scan has stored
  const resolvingRef = useRef(new Set());   // iglooIds with an in-flight on-demand fetch
  const mountedRef = useRef(true);          // true while mounted; guards setState after a real unmount

  // Set on mount (not just cleared on unmount) so StrictMode's simulated
  // mount→cleanup→mount restores it — a ref persists across that remount, so a
  // cleanup-only guard would stay false for the whole dev session.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // 1. Bulk scan — once per mount.
  useEffect(() => {
    if (!enabled || loadedRef.current) return undefined;
    let cancelled = false;

    const fetchIglooNames = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'iglooInfo'));
        const entries = await Promise.all(
          querySnapshot.docs.map((iglooDoc) =>
            resolveOne(iglooDoc.id, iglooDoc.data().controllers?.[0])
          )
        );
        if (!cancelled) {
          // Merge (not replace) so on-demand entries resolved meanwhile survive.
          setIglooNames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
          loadedRef.current = true; // suppress future scans only after a successful store
        }
      } catch (error) {
        console.error('useIglooNames: error fetching igloo names:', error);
        // loadedRef stays false → a later enable retries.
      }
    };

    fetchIglooNames();
    return () => { cancelled = true; };
  }, [enabled]);

  // 2. On-demand resolution of displayed igloo IDs the bulk scan didn't cover.
  // Content-keyed (not reference-keyed) so a stable set doesn't re-fire. Gated on
  // loadedRef so it only picks up the tail after the scan warms the common case;
  // the scan's store bumps `iglooNames`, which re-runs this effect.
  const neededKey = [...neededIds].sort().join(',');
  useEffect(() => {
    if (!enabled || !loadedRef.current || !neededKey) return;
    const missing = neededKey
      .split(',')
      .filter((id) => id && !(id in iglooNames) && !resolvingRef.current.has(id));
    if (missing.length === 0) return;

    missing.forEach((id) => resolvingRef.current.add(id));

    (async () => {
      try {
        const entries = await Promise.all(
          missing.map(async (iglooId) => {
            try {
              const iglooSnap = await getDoc(doc(db, 'iglooInfo', iglooId));
              const controllerFamilyId = iglooSnap.exists() ? iglooSnap.data().controllers?.[0] : null;
              return await resolveOne(iglooId, controllerFamilyId);
            } catch (e) {
              return [iglooId, iglooId];
            }
          })
        );
        // Merge unconditionally (idempotent, keyed by id) so a mid-fetch effect
        // re-run can't strand an in-flight id; skip only if the component unmounted.
        if (mountedRef.current) {
          setIglooNames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        }
      } finally {
        missing.forEach((id) => resolvingRef.current.delete(id));
      }
    })();
  }, [enabled, neededKey, iglooNames]);

  return iglooNames;
}
